import React, { useEffect, useRef, useState } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
  Box, Button, Divider, HStack, Input, Menu, MenuButton, MenuItem, MenuList, Portal,
  Select, SimpleGrid, Switch, Text, Textarea, VStack,
  useColorModeValue, useDisclosure, useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { useQueryClient } from 'react-query';
import {
  FiCheck, FiChevronDown, FiChevronUp, FiDollarSign, FiDownload, FiEye, FiRefreshCw, FiUser, FiX,
} from 'react-icons/fi';
import { FaFilePdf } from 'react-icons/fa';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { ValidationErrors } from 'final-form';
import { NIL } from 'uuid';
import MarkdownTextInput from './MarkdownTextInput';
import { RichTextView } from './RichTextEditor';
import ToggleInput from './ToggleInput';
import DateInputControl from './DateInput';
import TextInputControl from './TextInput';
import SelectInputControl from './SelectInput';
import { US_STATES } from '../../models/usStates';
import CurrencyInputControl from './CurrencyInput';
import FormRow from './FormRow';
import RnwyVenueBuilder from './RnwyVenueBuilder'; // RNWY 2.0 · P2 — the answer-once venue builder
import RnwyCapacityStrip from './RnwyCapacityStrip'; // item 861 — live box capacity vs this quote
import RnwyOptionsPreview from './RnwyOptionsPreview'; // RNWY 2.0 · P4 — live engine pricing preview
import RnwyProposalCfgPanel from './RnwyProposalCfgPanel'; // item 637 — per-quote CFG editor
import RnwySubscriptionEnhancementsNote from '../RnwySubscriptionEnhancementsNote'; // item 1544
import RnwyManualPricingPanel, { RnwyManualBadge } from './RnwyManualPricingPanel'; // item 954
import { parseVenues } from './rnwyVenues'; // item 647 — progress bar reads venue completion
import { parseCfg, money } from './rnwyCfg'; // item 654 — CFG snapshot for the SOW auto-builder
import usePricingSettings from '../../hooks/usePricingSettings'; // item 666 — company markup default
import { DEFAULT_PRICING_SETTINGS } from '../../models/pricingSettings';
import { composeFullScope } from './rnwyScopeBuilder';
import { mergeScopeWithReport } from './sowMerge'; // items 1098/1112 // item 654 — pure full-SOW composition
import useContact from '../../hooks/useContact'; // item 647 — integrator legal info from the customer
import useProject from '../../hooks/useProject'; // item 647 — resolve the proposal's customer via its project
import { ContactInfo, allSignatories } from '../../models/contactInfo';
import ResponsiveStack from '../ResponsiveStack';
import DocumentFormScaffold from './DocumentFormScaffold';
import ProjectSelectField from './ProjectSelectField';
import DocumentPlatformField from './DocumentPlatformField';
import CustomerProjectManagerField from './CustomerProjectManagerField';
import ProjectManagerField from './ProjectManagerField'; // item 833/C1
import ServiceAddressSection from './ServiceAddressSection';
import IconProposals from '../../assets/Icon-Proposals.svg';
import IconProposalsWhite from '../../assets/Icon-Proposals-White.svg';
import useProposal from '../../hooks/useProposal';
import useCompanyTerms from '../../hooks/useCompanyTerms';
import useCompanyRnwy from '../../hooks/useCompanyRnwy'; // item 613/614 — RNWY standardized language default
import { Proposal, ProposalItem, ProposalStatus } from '../../models/proposal';
import ProposalItemsFormElement from '../ProposalItemsFormElement';
import { openDocumentPdf } from './ViewPdfButton'; // item 1033 — PDF actions live in the footer menu now
import ProposalSendControl from '../ProposalSendControl';
import ProposalApprovalWizard from '../ProposalApprovalWizard';
import ReceivedPoAttachments from '../ReceivedPoAttachments';
import ProjectDocumentsSection from '../ProjectDocumentsSection'; // item 839 — proposal docs, shared with the project tree
import DocumentStatusStamp from '../DocumentStatusStamp';
import NewProposalChoice from '../NewProposalChoice'; // RNWY 2.0 · P0 — the Add-Proposal fork
import { addressLine } from '../../utils/addressLine'; // item 832 — flatten structured Address

const validate = (values: Proposal): ValidationErrors => {
  const errors: ValidationErrors = {};
  if (!values.proposalTitle) {
    errors.proposalTitle = 'Required';
  }
  // a proposal must belong to a project — without it the create is rejected 400
  if (!values.projectId) {
    errors.projectId = 'Required';
  }
  // number is optional — left blank, the backend auto-generates it (item 101)
  if (!values.date) {
    errors.date = 'Required';
  }
  // item 597/647 — Platform is REQUIRED for a standard proposal (manual entry). For an AUTOMATED
  // proposal it's auto-derived from the RNWY modules on save (see applyScaffoldState), so no manual
  // platform is required.
  const automated = !!(values as { isRnwy?: boolean }).isRnwy
    || (values as { kind?: number }).kind === 1 || (values as { kind?: number }).kind === 2;
  if (!automated && !values.platform?.trim()) {
    errors.platform = 'Required';
  }
  // item 647 — an automated proposal must have a SIGNATORY chosen (from the customer's list) in
  // Step 1. The dropdown snapshots the pick into integratorSignatoryName.
  if (automated && !(values as { integratorSignatoryName?: string }).integratorSignatoryName?.trim()) {
    (errors as { integratorSignatoryName?: string }).integratorSignatoryName = 'Required';
  }
  // item 553 — a proposal must attach a T&C option (None/0 is not allowed). Customer PM stays
  // OPTIONAL by design (Chad): it isn't tied to the commissions engine, and hard-requiring it
  // would block proposals for customers that have no CRM staff yet.
  if (!values.termsOption) {
    (errors as { termsOption?: string }).termsOption = 'Required';
  }
  return errors;
};

// item 239 — when RNWY is checked, auto-select the RNWY Terms & Conditions (option 2).
// Only fills when no option is chosen yet, so a manual pick isn't clobbered. Rendered
// inside the form so it can use the final-form API.
const RnwyTermsSync: React.FC = () => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const isRnwy = !!(values as { isRnwy?: boolean })?.isRnwy;
  const prev = useRef(isRnwy);
  useEffect(() => {
    const currentTerms = Number((values as { termsOption?: number })?.termsOption) || 0;
    if (isRnwy && !prev.current && currentTerms === 0) {
      form.change('termsOption', 2);
    }
    prev.current = isRnwy;
  }, [isRnwy, form, values]);
  return null;
};

// item 613 (REVISES 601) — the RNWY standard language NO LONGER drops into the Statement of
// Work (the SOW stays for the real per-job scope). Instead, turning the RNWY toggle ON pre-fills
// the dedicated "RNWY Standardized Language" section from the Settings-managed default — but ONLY
// when that section is still empty (never clobber an edit) and only on the OFF→ON transition.
// Rendered inside the form for the final-form API.
const RnwyStandardLanguageSync: React.FC = () => {
  const form = useForm();
  const { rnwy } = useCompanyRnwy();
  const { values } = useFormState({ subscription: { values: true } });
  const isRnwy = !!(values as { isRnwy?: boolean })?.isRnwy;
  // item 621 — pre-fill reliably. The old logic only fired on an OFF→ON toggle, so a proposal
  // that was ALREADY RNWY at load (or where the Settings default loaded late) never filled and
  // the section rendered blank (Chad's screenshot). Fill the section from the Settings default
  // exactly ONCE per RNWY-on session when it's still empty — this catches the already-on and
  // late-load cases while still respecting a deliberate clear (didFill guards re-clobbering).
  const didFill = useRef(false);
  useEffect(() => {
    if (!isRnwy) { didFill.current = false; return; }
    const current = ((values as { rnwyStandardLanguage?: string })?.rnwyStandardLanguage ?? '').trim();
    const defaultText = rnwy?.standardLanguage ?? '';
    if (!didFill.current && current === '' && defaultText) {
      form.change('rnwyStandardLanguage', defaultText);
      didFill.current = true;
    }
  }, [isRnwy, form, values, rnwy]);
  return null;
};

// item 431 — the after-approval hand-off editor: its own save path (the rest of the
// proposal stays locked as the contract)
const HandOffNotesEditor: React.FC<{ proposalId: string; initial: string }> = ({ proposalId, initial }) => {
  const [notes, setNotes] = React.useState(initial);
  const [saving, setSaving] = React.useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();
  const dirty = notes !== initial;
  const save = async () => {
    setSaving(true);
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      await axios.post(`${apiUrl}Proposal/${proposalId}/handoff-notes`, { notes });
      queryClient.invalidateQueries('proposal');
      toast({ title: 'Hand-off notes saved', status: 'success' });
    } catch {
      toast({ title: 'Save failed', description: 'Hand-off notes were NOT saved.', status: 'error' });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Box py={2}>
      <Textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        minH="120px"
        placeholder="Deployment notes for the install team"
      />
      <HStack mt={2} justify="flex-end">
        <Button size="sm" onClick={save} isLoading={saving} isDisabled={!dirty}>
          Save hand-off notes
        </Button>
      </HStack>
    </Box>
  );
};

// RNWY 2.0 · P0 — when a NEW proposal is opened with ?kind=rnwy (from the fork chooser), pre-set
// the RNWY toggle on so the RNWY template shows + persists (isRnwy). Standard/Crestron need no
// pre-set today (Crestron's persisted kind arrives with the ProposalKind migration slice). Fires once.
// item 711 (Chad) — on a NEW proposal, bring the PROPERTY NAME + ADDRESS over from the selected
// project's service address, so the proposal's Service Address starts pre-filled. It follows the
// project selection (re-fills when you switch projects) until you edit the name yourself — we only
// overwrite the name/address we last auto-filled, never a value you typed.
// item 954 — while a proposal is PRICED EXTERNALLY, engine-driven surfaces (CFG rate card,
// options preview) gray out so nobody reads engine numbers on a manual deal. The content stays
// visible (context) but inert, with the badge explaining why.
const RnwyManualGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { values } = useFormState({ subscription: { values: true } });
  const manual = !!(values as { rnwyManualPricingJson?: string | null }).rnwyManualPricingJson;
  if (!manual) return <>{children}</>;
  return (
    <Box position="relative">
      <Box opacity={0.35} pointerEvents="none" aria-disabled>
        {children}
      </Box>
      <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center">
        <HStack bg="blackAlpha.700" px={4} py={2} borderRadius="md" spacing={3}>
          <RnwyManualBadge />
          <Text fontSize="sm" color="white">Engine paused — this deal is priced externally.</Text>
        </HStack>
      </Box>
    </Box>
  );
};

const ProjectAddressSync: React.FC = () => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const v = values as { projectId?: string };
  const { data: project } = useProject({ id: v.projectId || NIL });
  const lastProjectId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!project || !v.projectId) return;
    // item 833/B9 (Chad) — do NOT auto-fill the proposal TITLE from the project. It inherited a
    // previous quote's name (the project name/service name); the user names each proposal for its
    // own scope instead. Only the service ADDRESS is prefilled from the project.
    if (v.projectId !== lastProjectId.current) {
      // item 833/B7 (Chad) — set each address SUBFIELD individually (not the whole object). Setting
      // the parent `serviceAddress` object doesn't repaint the registered child inputs, so they held
      // the value but rendered blank until you clicked each cell. Per-field change paints immediately.
      const a = (project.serviceAddress ?? {}) as {
        street?: string; unit?: string; city?: string; state?: string; zip?: string; country?: string;
      };
      form.batch(() => {
        form.change('serviceAddress.street', a.street || '');
        form.change('serviceAddress.unit', a.unit || '');
        form.change('serviceAddress.city', a.city || '');
        form.change('serviceAddress.state', a.state || '');
        form.change('serviceAddress.zip', a.zip || '');
        form.change('serviceAddress.country', a.country || '');
      });
    }
    lastProjectId.current = v.projectId;
  }, [project, v.projectId, form]);
  return null;
};

const NewProposalKindSync: React.FC<{ isNew: boolean; kind: string | null }> = ({ isNew, kind }) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !isNew || !kind) return;
    // persist the fork template (0 Standard / 1 RNWY / 2 Crestron)
    const kindInt = (({ rnwy: 1, crestron: 2 } as Record<string, number>)[kind]) ?? 0;
    form.change('kind', kindInt);
    if (kind === 'rnwy' && !(values as { isRnwy?: boolean }).isRnwy) {
      form.change('isRnwy', true); // legacy flag kept in sync so the RNWY sections show
    }
    done.current = true;
  }, [isNew, kind, form, values]);
  return null;
};

// RNWY 2.0 · item 632 — the FORK's `kind` drives the mode; there is no manual RNWY toggle. A
// kind=rnwy proposal (new OR existing) IS RNWY: keep the legacy `isRnwy` flag synced from the
// kind so the RNWY sections render and the backend RNWY behavior (T&C Option 2, PDF branding)
// follows the fork choice. Crestron (kind 2) is NOT RNWY; Standard (kind 0) strips RNWY entirely.
const RnwyModeFromKind: React.FC = () => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const { rnwy } = useCompanyRnwy();
  const v = values as { kind?: number; isRnwy?: boolean; rnwyCfgJson?: string | null };
  const isRnwy = !!v.isRnwy;
  const isEngine = v.kind === 1 || v.kind === 2 || isRnwy;
  useEffect(() => {
    if (v.kind === 1 && !isRnwy) form.change('isRnwy', true);
  }, [v.kind, isRnwy, form]);
  // dart (2026-07-16, Chad) — FREEZE the CFG snapshot the moment a NEW engine proposal is opened, so
  // its pricing locks to the Settings CFG AT CREATION and never re-pulls live on refresh/edit. An
  // existing proposal already carries its saved snapshot (spread into initialValues), so the guard
  // (empty rnwyCfgJson) prevents any overwrite. Duplicating a proposal copies its snapshot, so the
  // guard also leaves that intact — the copy prices off the source's CFG, exactly as Chad wants.
  useEffect(() => {
    if (isEngine && !v.rnwyCfgJson && rnwy?.cfgJson) form.change('rnwyCfgJson', rnwy.cfgJson);
  }, [isEngine, v.rnwyCfgJson, rnwy, form]);
  // dart (2026-07-16, Chad) — REGISTER rnwyVenuesJson + rnwyCfgJson as tracked fields (they were only
  // ever set via form.change, never registered). Unregistered fields aren't protected by
  // keepDirtyOnReinitialize, so the post-auto-save refetch RESET them to the stale doc value — wiping a
  // just-added venue add-on / re-pulling the CFG. Registering them (hidden, no UI) makes final-form
  // track their dirty state so an in-flight edit survives the reinitialize. Fixes the add-on "wipe on
  // refresh after save".
  return (
    <>
      <Field name="rnwyVenuesJson" subscription={{}}>{() => null}</Field>
      <Field name="rnwyCfgJson" subscription={{}}>{() => null}</Field>
    </>
  );
};

// item 662 — Step-3 "Suggested resale markup": edits the proposal's CFG snapshot
// (rnwyCfgJson.suggestedResaleMarkup). A multiplier > 1 makes the PDF print a muted suggested-resale
// figure (group × this) under each option + help-desk tier. Seeded from the global CFG default,
// overridable here per proposal. 0 / 1 = don't show it.
const RnwySuggestedMarkupField: React.FC = () => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const { rnwy } = useCompanyRnwy();
  // dart (2026-07-15, Chad) — SINGLE source: the RNWY rate card's own Suggested resale markup
  // (Settings → RNWY CFG), snapshotted onto the proposal's rnwyCfgJson at create. The old code also
  // fell back to Settings → Pricing's SRP markup, which leaked a mystery "2" that didn't match the
  // CFG. Now the proposal just pulls the CFG value and lets you override it per proposal. A local
  // DRAFT keeps it freely editable (decimals, retype) instead of snapping back mid-edit.
  const [draft, setDraft] = useState<string | null>(null);
  const cfg = parseCfg((values as { rnwyCfgJson?: string | null }).rnwyCfgJson || rnwy?.cfgJson);
  if (!cfg) return null;
  // dart (2026-07-15, Chad) — the proposal's OWN override wins; otherwise fall back to the LIVE
  // company CFG value so a Settings → RNWY change (e.g. 0 → 1.4) reflects on proposals that haven't
  // overridden — the proposal snapshotted 0 at create and would otherwise never see the new default.
  const proposalOwn = parseCfg((values as { rnwyCfgJson?: string | null }).rnwyCfgJson)?.suggestedResaleMarkup ?? 0;
  const companyLive = parseCfg(rnwy?.cfgJson)?.suggestedResaleMarkup ?? 0;
  const effective = proposalOwn > 0 ? proposalOwn : companyLive;
  const shown = draft ?? (effective ? String(effective) : '');
  const commit = (raw: string): void => {
    const v = raw === '' ? 0 : Number(raw);
    if (!Number.isNaN(v)) form.change('rnwyCfgJson', JSON.stringify({ ...cfg, suggestedResaleMarkup: v }));
  };
  return (
    <FormRow
      fullWidth
      heading="Suggested resale markup"
      subHeading="Multiplier for the client-facing 'suggested resale' price (group × this) on the PDF. Comes from the RNWY rate card (Settings → RNWY → Suggested resale markup); override per proposal here. Blank or ≤ 1 hides the suggested-resale figure."
    >
      <Input
        type="number"
        step="0.01"
        min={0}
        maxW="160px"
        value={shown}
        placeholder={companyLive > 0 ? String(companyLive) : 'off (set in Settings → RNWY)'}
        aria-label="suggested resale markup"
        onFocus={(e) => { setDraft(shown); e.currentTarget.select(); }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { if (draft !== null) { commit(draft); setDraft(null); } }}
      />
    </FormRow>
  );
};

// dart (2026-07-15, Chad) — STANDARD proposal Help Desk offer. Toggle ON → seed the three tier
// prices from the company CFG (first-venue tiers × the help-desk multiplier = group price) into
// helpDeskTierPricesJson; OFF → clear. Perpetual + non-RNWY jobs share these same choices. The
// client picks one (or declines) at approval — a monthly fee, 2-year term (see terms). The recurring
// subscription is enrolled MANUALLY on a standard quote.
const StandardHelpDeskField: React.FC = () => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const { rnwy } = useCompanyRnwy();
  const raw = (values as { helpDeskTierPricesJson?: string | null }).helpDeskTierPricesJson;
  const enabled = !!raw && raw.trim().length > 0;
  const cfg = parseCfg(rnwy?.cfgJson);
  const fv = cfg?.helpDesk?.firstVenue;
  const mult = cfg?.helpDeskFirstIncrease ?? 1;
  const tierPrices = {
    gold: (fv?.gold ?? 0) * mult,
    platinum: (fv?.platinum ?? 0) * mult,
    titanium: (fv?.titanium ?? 0) * mult,
  };
  // stored prices (a punt carries the RNWY quote's; else the live CFG tiers above)
  let shown = tierPrices;
  try { if (raw) shown = { ...tierPrices, ...(JSON.parse(raw) as typeof tierPrices) }; } catch { /* keep live */ }
  const toggle = (on: boolean): void => {
    form.change('helpDeskTierPricesJson', on ? JSON.stringify(tierPrices) : null);
  };
  // item 835/840 (Chad) — tier prices are EDITABLE per quote: prefilled from the CFG when Help Desk
  // is turned on, but any edit LOCKS to this quote (persisted in helpDeskTierPricesJson — item 833/B5).
  // A per-tier DRAFT string makes typing/clearing reliable (a raw controlled number input coerced
  // empty→0 mid-edit, which silently hid the tier and cost the edit). Commit the number on blur;
  // blank or 0 = that tier is HIDDEN on the PDF, > 0 = shown. Gold/Platinum/Titanium independent.
  const [tierDraft, setTierDraft] = useState<Record<string, string>>({});
  const commitTier = (key: 'gold' | 'platinum' | 'titanium', raw2: string): void => {
    const n = Number(raw2);
    form.change('helpDeskTierPricesJson', JSON.stringify({ ...shown, [key]: Number.isFinite(n) && n > 0 ? n : 0 }));
    setTierDraft((d) => { const next = { ...d }; delete next[key]; return next; });
  };
  return (
    <FormRow
      fullWidth
      heading="Help Desk (optional monthly add-on)"
      subHeading="Offer Help Desk on this quote. When on, all three tiers are presented and the client picks one — or declines — at approval (a monthly fee, 2-year term; see terms). Same tiers as a Perpetual RNWY job; the subscription is enrolled manually."
    >
      <HStack>
        <Switch isChecked={enabled} onChange={(e) => toggle(e.target.checked)} aria-label="offer help desk" />
        <Text fontSize="sm">{enabled ? 'Help Desk offered' : 'Help Desk off'}</Text>
      </HStack>
      {enabled && (
        <VStack align="stretch" mt={3} spacing={2} maxW="300px">
          {/* item 835 (Chad) — editable per-quote tier prices */}
          {(['gold', 'platinum', 'titanium'] as const).map((tier) => (
            <HStack key={tier} justify="space-between">
              <Text fontSize="sm" textTransform="capitalize" minW="72px">{tier}</Text>
              <HStack spacing={1}>
                <Text fontSize="sm" color="gray.500">$</Text>
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  maxW="110px"
                  value={tierDraft[tier] ?? String(shown[tier] ?? 0)}
                  onChange={(e) => setTierDraft((d) => ({ ...d, [tier]: e.target.value }))}
                  onBlur={(e) => commitTier(tier, e.target.value)}
                />
                <Text fontSize="sm" color="gray.500">/mo</Text>
              </HStack>
            </HStack>
          ))}
          <Text fontSize="xs" color="gray.500">Prefilled from settings — edit for a one-off custom price. Set a tier to 0 to hide it on the PDF; any tier above $0 shows.</Text>
        </VStack>
      )}
    </FormRow>
  );
};

// item 833 (Chad) — the "Show Help Desk on PDF" output toggle only exists once Help Desk is
// enabled for the proposal (StandardHelpDeskField seeds helpDeskTierPricesJson). Rendered inside
// the Item Settings grid; returns nothing when Help Desk isn't offered.
const HelpDeskPdfToggle: React.FC = () => {
  const { values } = useFormState({ subscription: { values: true } });
  const enabled = !!(values as { helpDeskTierPricesJson?: string | null }).helpDeskTierPricesJson;
  if (!enabled) return null;
  return (
    <>
      <Field<boolean>
        name="showHelpDeskOnPdf"
        component={ToggleInput}
        defaultValue
        label="Show Help Desk on PDF"
        helperText="Show the optional Help Desk tiers on the PDF."
      />
      {/* item 1285 (Chad) — the definitions block is optional per quote */}
      <Field<boolean>
        name="showDefinitionsOnPdf"
        component={ToggleInput}
        format={(val) => val !== false}
        label="Show Help Desk definitions on PDF"
        helperText="The Help Desk definitions text that prints above the T&C."
      />
    </>
  );
};

// item 841 (Chad) — pick which category is billed MONTHLY. Its items are pulled out of the one-time
// build and shown in a separate Monthly section with a raw Monthly Total (no tax) on the PDF. Build
// the category above like any other, then select it here. "None" = no monthly section. Single bucket.
const MonthlyCategoryField: React.FC = () => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const v = values as { itemCategories?: string; monthlyCategory?: string | null };
  const cats = (v.itemCategories ?? '').split(',').map((c) => c.trim()).filter(Boolean);
  const current = v.monthlyCategory ?? '';
  return (
    <Box mt={4}>
      <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" textTransform="uppercase" opacity={0.7} mb={1}>
        Monthly section
      </Text>
      <Select
        size="sm"
        maxW="360px"
        value={current}
        onChange={(e) => form.change('monthlyCategory', e.target.value || null)}
      >
        <option value="">No monthly section</option>
        {cats.map((c) => <option key={c} value={c}>{`Bill "${c}" monthly`}</option>)}
      </Select>
      <Text fontSize="xs" color="gray.500" mt={1}>
        Items in the chosen category are billed monthly — pulled out of the one-time total and shown as
        a separate Monthly Total on the PDF. Add a category above (e.g. &quot;Monthly&quot;), then pick it here.
      </Text>
    </Box>
  );
};

// item 661 — RNWY Definitions: read-only display in Step 3 (above the T&C) of the tenant's
// Definitions default from Settings → RNWY. It also prints on the PDF above the T&C. Nothing shows
// until the copy is authored in Settings.
const RnwyDefinitionsSection: React.FC = () => {
  const { rnwy } = useCompanyRnwy();
  const text = rnwy?.definitions ?? '';
  // dart (2026-07-15, Chad) — NEXT_EPIC #6: collapsed by default (the definitions are long); expand
  // to review. Doesn't affect the PDF — this is just the on-screen editor display.
  const [open, setOpen] = useState(false);
  if (!text.trim()) return null;
  return (
    <FormRow
      fullWidth
      heading="Definitions"
      subHeading="Defined terms — printed above the Terms & Conditions. Edit in Settings → RNWY."
    >
      <Button
        size="sm"
        variant="ghost"
        leftIcon={open ? <FiChevronUp /> : <FiChevronDown />}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? 'Hide definitions' : 'Show definitions'}
      </Button>
      {open && <Box mt={2}><RichTextView source={text} /></Box>}
    </FormRow>
  );
};

// item 654 — RNWY Statement-of-Work manager. The SOW AUTO-BUILDS from the venue model + modules +
// SwitchPilot (composeFullScope) and keeps updating as you fill Steps 1–2 — so by the time you
// reach the SOW it's already written. The moment you EDIT the SOW body yourself, auto-updates pause
// (we never clobber your text). A CONFIRMED "Regenerate" rebuilds it from scratch (edits are lost).
// Ownership model: `lastAuto` = the exact string we last auto-wrote. If the current SOW still equals
// it, we own it and keep syncing; if it diverges, the user edited → pause. An existing proposal's
// saved SOW is respected on load (locked), never auto-clobbered.
const RnwyScopeManager: React.FC<{
  isNew: boolean;
  savedSow: string | null;
  loaded: boolean;
  savedVenuesJson: string | null; // item 1391 — the venues AS SAVED, to recognise untouched auto text
}> = ({
  isNew, savedSow, loaded, savedVenuesJson,
}) => {
  const form = useForm();
  const sowToast = useToast(); // item 1112 — name the sections a merge removes
  const { values } = useFormState({ subscription: { values: true } });
  const { rnwy } = useCompanyRnwy();
  const cfg = parseCfg((values as { rnwyCfgJson?: string | null }).rnwyCfgJson || rnwy?.cfgJson);
  const venuesJson = (values as { rnwyVenuesJson?: string | null }).rnwyVenuesJson;
  const curSow = (values as { statementOfWork?: string }).statementOfWork ?? '';
  const next = composeFullScope(venuesJson, cfg);
  const lastAuto = useRef<string | null>(null);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    // item 994 (Chad: "every time we add custom text the statement of work is overwritten") —
    // this effect used to run while an EXISTING proposal was still LOADING: the form's SOW read
    // empty, so it "adopted" and wrote composed text — and keepDirtyOnReinitialize then blocked
    // the SAVED custom SOW from ever loading; the next save stamped the composed text over the
    // user's copy. HARD RULES now: (1) never touch the form before the document is loaded;
    // (2) an existing proposal only auto-builds when its SAVED SOW is genuinely empty.
    if (!loaded) return;
    if (lastAuto.current === null) {
      const savedEmpty = isNew || (savedSow ?? '').trim() === '';
      if (savedEmpty && curSow.trim() === '') { form.change('statementOfWork', next); lastAuto.current = next; return; }
      // item 1391 (Chad: "once a quote is saved the auto save is not allowing any new updated
      // text generation") — a SAVED proposal always paused, even when nobody had ever typed in
      // the SOW: the saved text was the composer's own output, but on reload it looked like the
      // user's. Recompose from the venues AS SAVED; if that is exactly the saved text, it is still
      // ours and syncing resumes (a venue rename now flows straight into the heading). Any
      // difference means a human edited it → pause, exactly as before (994).
      const savedIsOurs = !isNew && savedVenuesJson != null && (savedSow ?? '') !== ''
        && composeFullScope(savedVenuesJson, cfg) === (savedSow ?? '');
      if (savedIsOurs && curSow === (savedSow ?? '')) {
        lastAuto.current = curSow;
        if (curSow !== next) { form.change('statementOfWork', next); lastAuto.current = next; }
        return;
      }
      setPaused(true);
      return;
    }
    if (curSow !== lastAuto.current) { setPaused(true); return; } // user edited → pause auto-updates
    if (curSow !== next) { form.change('statementOfWork', next); lastAuto.current = next; } // keep synced
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next, curSow, loaded]);

  // item 1400 (Chad: "scope quantity of x should always match the pricing and the scope") — a
  // QUANTITY is engine data, not prose. While auto-sync is paused for the user's writing, a venue
  // change (a switch count 69 → 50, a zone added, a size flipped) used to reach the text only if
  // someone clicked Update — PROP-100615 went out priced at 50 switches with a scope that said 69.
  // Now every venue-model change (and the first open of an edited SOW) runs the keep-my-text
  // merge automatically: headings, quantities and the switch count follow pricing, every sentence
  // the user wrote stays where it was. Rebuild-from-scratch remains the only way to lose text.
  const lastNextRef = useRef<string | null>(null);
  const openSyncedRef = useRef(false);
  // item 1439 (Chad: the blue toast "pops up with every keystroke, even pressing delete" while
  // typing a venue name) — a rename changes the venue model per character, and each change ran a
  // merge + a toast ("Confe → Confer → Confere…"). The merge now reads the LATEST venues/SOW via
  // refs and runs once the model has been quiet for a moment; the toast carries a fixed id so a
  // newer merge replaces the previous notice instead of stacking ten of them.
  const nextRef = useRef(next); nextRef.current = next;
  const curSowRef = useRef(curSow); curSowRef.current = curSow;
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const SOW_SYNC_TOAST_ID = 'sow-scope-sync';
  const syncScopeToPricing = (): void => {
    const latestNext = nextRef.current;
    const latestSow = curSowRef.current;
    if (latestSow.trim() === '' || latestNext.trim() === '') return;
    const { text: merged, dropped, renamed } = mergeScopeWithReport(latestNext, latestSow);
    if (merged === latestSow) return;
    form.change('statementOfWork', merged);
    lastAuto.current = merged === latestNext ? latestNext : null;
    setPaused(merged !== latestNext);
    const bits: string[] = [];
    if (renamed.length > 0) bits.push(`renamed: ${renamed.join(' · ')}`);
    if (dropped.length > 0) bits.push(`removed (no longer in the quote): ${dropped.join(' · ')}`);
    if (sowToast.isActive(SOW_SYNC_TOAST_ID)) sowToast.close(SOW_SYNC_TOAST_ID);
    sowToast({
      id: SOW_SYNC_TOAST_ID,
      status: 'info',
      title: 'Scope quantities updated to match pricing',
      description: bits.length > 0 ? `Your text was kept. ${bits.join('; ')}.` : 'Your text was kept; the generated quantities and headings now match the venue model.',
      duration: 6000,
      isClosable: true,
    });
  };
  useEffect(() => {
    if (!loaded) return;
    if (!paused) { lastNextRef.current = next; return; } // unpaused = the first effect keeps the whole text in sync
    if (lastNextRef.current === null) lastNextRef.current = next;
    // first open of an edited (paused) SOW: bring its quantities in line once (immediately)
    if (!openSyncedRef.current) { openSyncedRef.current = true; syncScopeToPricing(); return; }
    // a venue-model change while paused → one merge after the typing settles (item 1439)
    if (next !== lastNextRef.current) {
      lastNextRef.current = next;
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(() => { syncTimerRef.current = null; syncScopeToPricing(); }, 1200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next, loaded, paused]);
  useEffect(() => () => { if (syncTimerRef.current) clearTimeout(syncTimerRef.current); }, []); // item 1439 — no merge after unmount

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  // item 1098 (Tom's live-contract bug) — UPDATE = merge: refresh the generated structure
  // (headings incl. the system size, quantities, new/removed items) while keeping every word
  // the user wrote. Rebuild-from-scratch stays available, one click deeper, clearly labelled.
  const update = (): void => {
    // item 1112 (Chad: "we scoped it down but it's still pulling in trashed venues") — the merge
    // now removes sections whose venue is gone from the quote. Say WHICH, so a removal is never
    // silent: this is customer-facing scope, and text vanishing unannounced is its own bug.
    const { text: merged, dropped, renamed } = mergeScopeWithReport(next, curSow);
    form.change('statementOfWork', merged);
    lastAuto.current = merged === next ? next : null; // merged text is the user's now
    setPaused(merged !== next);
    // item 1391 — a renamed venue kept its text under the new heading; say so
    if (renamed.length > 0) {
      sowToast({
        status: 'info',
        title: `Renamed ${renamed.length} venue section${renamed.length === 1 ? '' : 's'} — your text kept`,
        description: renamed.join(' · '),
        duration: 6000,
        isClosable: true,
      });
    }
    if (dropped.length > 0) {
      sowToast({
        status: 'info',
        title: `Removed ${dropped.length} section${dropped.length === 1 ? '' : 's'} no longer in this quote`,
        description: dropped.join(' · '),
        duration: 7000,
        isClosable: true,
      });
    }
    onClose();
  };
  const rebuild = (): void => {
    form.change('statementOfWork', next);
    lastAuto.current = next;
    setPaused(false);
    onClose();
  };
  const hint = useColorModeValue('gray.500', 'whiteAlpha.600');
  const accent = useColorModeValue('orange.600', 'orange.300');
  // item 1098 — the SILENT-STALE trap: once you edit the SOW we stop syncing, so a later venue
  // change (Medium → Flex, a quantity, a new item) never reaches the text and the proposal goes
  // out describing the wrong system. Say so, loudly, the moment it happens.
  const stale = paused && curSow.trim() !== '' && next.trim() !== '' && curSow !== next;
  return (
    <Box mb={3}>
      <HStack justify="space-between" align="center" flexWrap="wrap" spacing={3}>
        <Text fontSize="xs" color={paused ? accent : hint} flex="1" minW="200px">
          {paused
            ? 'Auto-updates paused — you’ve edited this. Update keeps your text and refreshes the generated parts.'
            : 'Auto-building from your venues, modules & SwitchPilot as you fill them in.'}
        </Text>
        <Button size="xs" leftIcon={<FiRefreshCw />} colorScheme={stale ? 'orange' : undefined} variant={stale ? 'solid' : 'outline'} onClick={onOpen}>
          Update Statement of Work
        </Button>
      </HStack>
      {stale && (
        <Text fontSize="xs" color={accent} mt={1}>
          Your venues have changed since this text was written — the Statement of Work may
          describe the wrong system size or quantities. Update to bring it in line.
        </Text>
      )}
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
        <AlertDialogOverlay>
          {/* item 1394 (Chad: "it's off screen") — three footer buttons overflowed the default (md)
              card width and pushed Cancel outside the dialog's left edge. Wide enough for all three,
              and the footer wraps instead of overflowing on a narrow viewport. */}
          <AlertDialogContent maxW="640px" mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Update Statement of Work</AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={3}>
                This refreshes the generated parts — venue headings (including the system size),
                quantities, and any items you’ve added or removed —
                {' '}
                <b>and keeps everything you’ve written, on the lines where you wrote it</b>
                .
              </Text>
              {/* item 1392 (Chad: "I need a reality check") — the one honest rule of the merge */}
              <Text fontSize="sm" color={hint} mb={3}>
                The rule: a line you edited is still recognised as long as its first few generated
                words remain. A line you rewrote completely is treated as your own line and kept
                alongside the regenerated one.
              </Text>
              <Text fontSize="sm" color={hint}>
                Need a clean slate instead? “Rebuild from scratch” throws away your manual text and
                regenerates the whole document from your venues.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter flexWrap="wrap" justifyContent="flex-end">
              {/* item 1391 (Chad screenshot) — Cancel wore the tenant brand color (Pulse red) and read
                  like the destructive primary; it is the quiet exit, so it goes ghost + gray */}
              <Button ref={cancelRef} onClick={onClose} size="sm" variant="ghost" colorScheme="gray" mt={2}>Cancel</Button>
              <Button variant="ghost" colorScheme="red" onClick={rebuild} ml={3} size="sm" mt={2}>Rebuild from scratch</Button>
              <Button colorScheme="brandGreen" onClick={update} ml={3} size="sm" mt={2}>Update &amp; keep my text</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

// item 647 — the RNWY "platform" IS the enabled modules. For an automated build there's no manual
// platform entry — the Step-2 module toggles auto-derive it as "<modules> by RNWY" (RNWY = the OS /
// umbrella brand), which flows to the WO + invoice. (The manual platform box returns with the
// deferred non-RNWY sibling-quote feature.)
const PLATFORM_MODULE_LABELS: Record<string, string> = {
  concourse: 'Concourse', aviator: 'Aviator', switchPilot: 'SwitchPilot', thirdParty: 'Third-party integration',
};
const rnwyPlatformLabel = (venuesJson?: string | null): string => {
  const mods = parseVenues(venuesJson ?? undefined).jobLevel.modules as unknown as Record<string, boolean>;
  const on = Object.keys(PLATFORM_MODULE_LABELS).filter((k) => mods[k]).map((k) => PLATFORM_MODULE_LABELS[k]);
  if (!on.length) return '';
  const joined = on.length === 1 ? on[0] : `${on.slice(0, -1).join(', ')} & ${on[on.length - 1]}`;
  return `${joined} by RNWY`;
};
const RnwyPlatformDisplay: React.FC = () => {
  const { values } = useFormState({ subscription: { values: true } });
  const label = rnwyPlatformLabel((values as { rnwyVenuesJson?: string | null }).rnwyVenuesJson);
  const sub = useColorModeValue('gray.500', 'whiteAlpha.600');
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  if (!label) {
    return (
      <Text fontSize="sm" color={sub}>
        Toggle the RNWY modules in Step 2 — they set this job&apos;s platform automatically.
      </Text>
    );
  }
  return (
    <Box borderWidth="1px" borderColor={border} borderRadius="md" px={3} py={2}>
      <Text fontSize="sm" fontWeight="medium">{label}</Text>
    </Box>
  );
};

// item 647 — the RNWY guided-flow PROGRESS BAR, pinned in the sticky EDIT PROPOSAL header (so it
// stays visible while scrolling the steps). One segment per step, filling brand-green as each
// step's key fields are completed. It's rendered OUTSIDE the Form (in the header), so it receives
// the live values as a prop (the scaffold lifts them). Completion criteria are grounded in the
// form's required fields (see `validate`), distributed across the guided steps: Step 1 (Project
// Foundation) = Name + Project + Date; Step 2 (Venues) = at least one venue with a quantity;
// Step 3 (Scope) = Platform + a Terms option.
const RnwyStepProgress: React.FC<{
  values: Record<string, unknown>;
  // item 710 (Chad) — click a segment to open + scroll to that step (index matches the step order)
  goToStep?: (index: number) => void;
}> = ({ values, goToStep }) => {
  const v = values as unknown as Proposal & { rnwyVenuesJson?: string | null };
  const venuesFilled = (() => {
    try {
      return parseVenues(v.rnwyVenuesJson ?? undefined)
        .venues.some((ve) => Object.values(ve.quantities).some((q) => (q || 0) > 0));
    } catch {
      return false;
    }
  })();
    // dart (2026-07-14) — real per-step metrics + names. A FRESH proposal must read 0% — every gate
    // below is false on a blank quote (the old bar lit Step 3 off the auto-set Terms/modules defaults
    // and Step 5 was hardcoded true → a blank quote showed "2 of 5 · 40%"). Everything past
    // Foundation is gated on venuesFilled so RNWY defaults can't pre-complete a step.
  const termsChosen = !!(v as { termsOption?: number }).termsOption;
  const saved = !!v.id && v.id !== NIL;
  const steps = [
    // Foundation — the identifiers the quote hangs on
    { label: 'Foundation', done: !!(v.proposalTitle && v.projectId && v.date) },
    // Venues — at least one venue with real quantities
    { label: 'Venues', done: venuesFilled },
    // Scope — venues carry modules → a platform is derived
    { label: 'Scope', done: venuesFilled && !!rnwyPlatformLabel(v.rnwyVenuesJson) },
    // Pricing — a priced quote (venues) with a Terms option selected
    { label: 'Pricing', done: venuesFilled && termsChosen },
    // Output — the proposal is saved, ready to send/export
    { label: 'Output', done: saved },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);
  const trackBg = useColorModeValue('blackAlpha.200', 'whiteAlpha.200');
  const doneBg = useColorModeValue('brandGreen.500', 'brandGreen.300');
  const labelColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  return (
    <Box mb={4}>
      <HStack justify="space-between" mb={1.5}>
        <Text fontSize="xs" fontWeight="semibold" color={labelColor} textTransform="uppercase" letterSpacing="0.06em">
          Proposal progress
        </Text>
        <Text fontSize="xs" color={labelColor}>{`${doneCount} of ${steps.length} complete · ${pct}%`}</Text>
      </HStack>
      <HStack spacing={2} align="flex-start">
        {steps.map((s, i) => (
          <Box
            key={s.label}
            as="button"
            type="button"
            flex="1"
            textAlign="left"
            onClick={goToStep ? () => goToStep(i) : undefined}
            cursor={goToStep ? 'pointer' : 'default'}
            title={goToStep ? `Go to ${s.label}` : undefined}
            _hover={goToStep ? { opacity: 0.7 } : undefined}
            transition="opacity 0.2s ease"
          >
            <Box h="6px" borderRadius="full" bg={s.done ? doneBg : trackBg} transition="background 0.3s ease" />
            <Text fontSize="0.65rem" color={labelColor} mt={1}>{s.label}</Text>
          </Box>
        ))}
      </HStack>
    </Box>
  );
};

RnwyStepProgress.defaultProps = { goToStep: undefined };

// item 710 (Chad) — AUTOMATED (RNWY) output settings: which engine sections print on the PDF. All
// default ON (show everything). The three pricing-option toggles guard against hiding the last one
// (an automated proposal always shows at least one of Perpetual / 2-year / 5-year); the backend
// enforces the same rule as a safety net. Self-contained so it can read live values for the guard.
const RnwyOutputSection: React.FC = () => {
  const { values } = useFormState({ subscription: { values: true } });
  const v = values as Partial<Proposal>;
  const optOn = (b?: boolean) => b !== false; // undefined (unsaved) or true = visible
  const visibleOptions = [v.showPerpetualOnPdf, v.showTwoYearOnPdf, v.showFiveYearOnPdf]
    .filter(optOn).length;
  const lockLast = (b?: boolean) => optOn(b) && visibleOptions <= 1;
  return (
    <FormRow
      fullWidth
      heading="Output settings"
      subHeading="Choose which sections print on the proposal PDF — all on by default"
    >
      {/* item 1179 (Chad: "does not save the output settings from close to reopen") —
          NEVER defaultValue here: it set the value before the fetched doc arrived, marking
          the fields DIRTY, and keepDirtyOnReinitialize then blocked the SAVED values from
          loading (they looked ON, and the next auto-save overwrote the real settings).
          format treats undefined as ON for display only; mapValues stamps true at save. */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={1}>
        <Field<boolean>
          name="showPerpetualOnPdf"
          component={ToggleInput}
          format={(val) => val !== false}
          isDisabled={lockLast(v.showPerpetualOnPdf)}
          label="Show Perpetual option"
          helperText="Option 1 — the paid-once perpetual license."
        />
        <Field<boolean>
          name="showTwoYearOnPdf"
          component={ToggleInput}
          format={(val) => val !== false}
          isDisabled={lockLast(v.showTwoYearOnPdf)}
          label="Show 2-year option"
          helperText="Option 2 — the two-year subscription."
        />
        <Field<boolean>
          name="showFiveYearOnPdf"
          component={ToggleInput}
          format={(val) => val !== false}
          isDisabled={lockLast(v.showFiveYearOnPdf)}
          label="Show 5-year option"
          helperText="Option 3 — the five-year subscription."
        />
        {/* item 1544 — the subscription-only enhancements that print with any subscription option */}
        <RnwySubscriptionEnhancementsNote compact />
        <Field<boolean>
          name="showHelpDeskOnPdf"
          component={ToggleInput}
          format={(val) => val !== false}
          label="Show Help Desk"
          helperText="The Help Desk tier menu (and the all-in-with-Help-Desk pages)."
        />
        {/* item 1331 (Chad) — renamed from "Show definitions": it's the Help Desk definitions block */}
        <Field<boolean>
          name="showDefinitionsOnPdf"
          component={ToggleInput}
          format={(val) => val !== false}
          label="Show Help Desk definitions"
          helperText="The Help Desk definitions block printed above the T&C."
        />
        <Field<boolean>
          name="showSwitchPilotOnPdf"
          component={ToggleInput}
          format={(val) => val !== false}
          label="Show SwitchPilot"
          helperText="The SwitchPilot add-on card and its monthly line."
        />
        {/* item 1331 (Chad: "everything above One-time total should have an output option to hide
            pricing itemization; the SKUs need to still show up") */}
        <Field<boolean>
          name="showLinePricesOnPdf"
          component={ToggleInput}
          format={(val) => val !== false}
          label="Show itemized pricing"
          helperText="Off: the lines above each option's One-time total keep their SKU + quantity but print no per-line price — only the total."
        />
      </SimpleGrid>
    </FormRow>
  );
};

// item 647 — the integrator legal identity + signatory, shown READ-ONLY on the proposal and pulled
// from the CUSTOMER's CRM record (the integrator IS the customer). One source of truth: edit it on
// the customer, never per-proposal. Resolves the customer from the proposal's customerId, falling
// back to the selected project's customer for a brand-new proposal that hasn't saved a customerId.
const IntegratorLegalReadOnly: React.FC = () => {
  const form = useForm();
  const { values, errors, submitFailed } = useFormState({
    subscription: { values: true, errors: true, submitFailed: true },
  });
  const v = values as {
    customerId?: string; projectId?: string;
    integratorSignatoryName?: string; integratorSignatoryTitle?: string; integratorSignatoryEmail?: string;
  };
  // item 647 — the signatory pick is REQUIRED (validate). Surface it here since this is a plain
  // Select, not a react-final-form Field, so it doesn't auto-show a touched error.
  const sigError = !!submitFailed && !!(errors as { integratorSignatoryName?: string })?.integratorSignatoryName;
  const history = useHistory();
  const { data: project } = useProject({ id: v.projectId || NIL });
  const customerId = v.customerId || project?.customerId || '';
  const { data: customer } = useContact({ id: customerId || '' });
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  const sub = useColorModeValue('gray.500', 'whiteAlpha.600');
  const labelColor = useColorModeValue('gray.600', 'whiteAlpha.700');

  // dart (2026-07-15, Chad) — the Signatory is a plain <Select>, not a Field, matched by
  // name+title+email. On a fresh proposal the form value is empty, so nothing is "picked" — but a
  // browser shows the first option under the disabled placeholder, so it LOOKS chosen while the form
  // stays empty and "Please select a signatory" never clears. Auto-adopt the customer's PRIMARY
  // signatory when none is picked yet (one source of truth), so the value matches what's shown and
  // the required-field validation passes. No-op once a signatory is already set (won't fight an edit).
  useEffect(() => {
    const list = allSignatories(customer as ContactInfo | undefined);
    if (list.length === 0) return;
    const alreadyPicked = list.some(
      (s) => s.name === (v.integratorSignatoryName || '')
        && s.title === (v.integratorSignatoryTitle || '')
        && s.email === (v.integratorSignatoryEmail || ''),
    );
    if (alreadyPicked || (v.integratorSignatoryName || '').trim()) return;
    const s = list[0];
    form.change('integratorSignatoryName', s.name);
    form.change('integratorSignatoryTitle', s.title);
    form.change('integratorSignatoryEmail', s.email);
  }, [customer, v.integratorSignatoryName, v.integratorSignatoryTitle, v.integratorSignatoryEmail, form]);

  if (!customerId) {
    return (
      <Text fontSize="xs" color={sub}>
        Select a project first — the integrator&apos;s legal info comes from that customer&apos;s record.
      </Text>
    );
  }
  const c = customer as ContactInfo | undefined;
  // read-only legal identity (one legal entity per customer)
  const legalRows: [string, string][] = [
    ['Legal name', c?.integratorLegalName || ''],
    // item 832 — the customer's corporate address is structured now; show it as one line
    ['Corporate address', addressLine(c?.integratorCorporateAddress)],
  ];
  // item 647 — the per-proposal SIGNATORY pick: primary + alternates from the customer's record
  const signatories = allSignatories(c);
  const selectedIdx = signatories.findIndex(
    (s) => s.name === (v.integratorSignatoryName || '')
      && s.title === (v.integratorSignatoryTitle || '')
      && s.email === (v.integratorSignatoryEmail || ''),
  );
  const pick = (idx: number) => {
    const s = signatories[idx];
    if (!s) return;
    form.change('integratorSignatoryName', s.name);
    form.change('integratorSignatoryTitle', s.title);
    form.change('integratorSignatoryEmail', s.email);
  };
  return (
    <Box borderWidth="1px" borderColor={border} borderRadius="md" p={3}>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={2} mb={3}>
        {legalRows.map(([label, val]) => (
          <Box key={label}>
            <Text fontSize="xx-small" textTransform="uppercase" letterSpacing="0.05em" color={labelColor}>{label}</Text>
            <Text fontSize="sm">{val || '—'}</Text>
          </Box>
        ))}
      </SimpleGrid>
      <Box>
        <Text fontSize="xx-small" textTransform="uppercase" letterSpacing="0.05em" color={sigError ? 'red.400' : labelColor} mb={1}>
          Signatory *
        </Text>
        {signatories.length === 0 ? (
          <Text fontSize="sm" color={sigError ? 'red.400' : sub}>
            No signatories on this customer yet — add one on the customer&apos;s record to select it here.
          </Text>
        ) : (
          <>
            <Select
              size="sm"
              maxW="360px"
              value={selectedIdx >= 0 ? String(selectedIdx) : ''}
              onChange={(e) => pick(Number(e.target.value))}
              aria-label="Signatory"
              isInvalid={sigError}
            >
              <option value="" disabled>Select a signatory…</option>
              {signatories.map((s, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <option key={`${s.email}-${i}`} value={i}>
                  {s.name || '(unnamed)'}
                  {s.title ? ` — ${s.title}` : ''}
                </option>
              ))}
            </Select>
            {sigError && (
              <Text fontSize="xs" color="red.400" mt={1}>Please select a signatory.</Text>
            )}
            {selectedIdx >= 0 && signatories[selectedIdx].email && (
              <Text fontSize="xs" color={sub} mt={1}>{signatories[selectedIdx].email}</Text>
            )}
          </>
        )}
      </Box>
      <Button size="xs" variant="link" mt={2} onClick={() => history.push(`/customer/${customerId}`)}>
        Edit on the customer record →
      </Button>
    </Box>
  );
};

// dart (2026-07-14) — Step 1 "Customer" block: surfaces WHO the proposal is for (the customer on
// its project), on BOTH automated + standard proposals, kept distinct from the Service Address (the
// end-user site, which can differ for a drop-ship / multi-site job). If the proposal has been MOVED
// to a project owned by a DIFFERENT customer, its saved customer snapshot (CustomerId + integrator
// legal identity + signatory + customer PM) goes stale — we detect that (stored customerId vs the
// project's current customerId) and offer a one-click "update customer information" that re-pulls
// the customer-linked fields from the backend (proposal/{id}/refresh-customer) WITHOUT touching the
// service address, pricing, venues, scope, or T&C.
const CustomerSection: React.FC<{ locked?: boolean }> = ({ locked }) => {
  const { id } = useParams<{ id: string }>();
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const v = values as { projectId?: string; customerId?: string };
  const queryClient = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const { data: project } = useProject({ id: v.projectId || NIL });
  const projectCustomerId = project?.customerId || '';
  const { data: projectCustomer } = useContact({ id: projectCustomerId || '' });
  const storedCustomerId = v.customerId || '';
  const { data: storedCustomer } = useContact({ id: storedCustomerId || '' });

  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  const sub = useColorModeValue('gray.500', 'whiteAlpha.600');
  const labelColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  const warnBg = useColorModeValue('orange.50', 'whiteAlpha.100');

  const c = projectCustomer as ContactInfo | undefined;
  const displayName = c?.name || project?.customerName || '';

  // mismatch = the proposal's SAVED customer differs from the customer that now owns its project
  // (a moved proposal). Only meaningful once saved (id !== NIL), with both ids resolved, and while
  // the proposal is still EDITABLE — a locked (approved/complete/void) proposal is frozen by design
  // (duplicate it to revise), so re-pulling the customer would just 400 "locked".
  const mismatch = id !== NIL && !locked && !!storedCustomerId && !!projectCustomerId
    && storedCustomerId !== projectCustomerId;
  const oldName = (storedCustomer as ContactInfo | undefined)?.name;

  // dart (2026-07-14) — pop the confirm modal the moment the user SWITCHES to a project owned by a
  // different customer (more intuitive than hoping they spot the banner — Chad). Track the last
  // project we saw; when it changes to one whose customer differs, open the modal. The mismatch
  // gate means it never fires on initial load or a same-customer switch.
  const lastProject = useRef<string | undefined>();
  const revertTo = useRef<string | undefined>(); // the project to snap back to if they Cancel
  const [switched, setSwitched] = useState(false);
  useEffect(() => {
    const prev = lastProject.current;
    lastProject.current = v.projectId;
    if (prev !== undefined && v.projectId && v.projectId !== prev) {
      revertTo.current = prev;
      setSwitched(true);
    }
  }, [v.projectId]);
  useEffect(() => {
    // the switched-to project's customer resolves async — wait for the mismatch to actually confirm
    if (switched && mismatch) {
      setConfirmOpen(true);
      setSwitched(false);
    }
  }, [switched, mismatch]);

  // Cancel = back out of the switch entirely: snap the Project dropdown back to where they were
  // (Chad). Reverting flips projectId back so the customer matches again → the banner clears too.
  const cancel = () => {
    if (revertTo.current) form.change('projectId', revertTo.current);
    setConfirmOpen(false);
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      // pass the currently-selected project so the endpoint MOVES + syncs atomically — correct even
      // if the form hasn't auto-saved the move yet (no save-timing race).
      await axios.post(`${apiUrl}proposal/${id}/refresh-customer`, { projectId: v.projectId });
      // clear the flag + refresh the live-resolved integrator block immediately, then refetch so the
      // signatory/PM the backend just re-pulled show on the form.
      if (projectCustomerId) form.change('customerId', projectCustomerId);
      await queryClient.invalidateQueries(['proposal', id]);
      await queryClient.invalidateQueries('proposals');
      await queryClient.invalidateQueries(['project']);
      toast({
        title: 'Customer information updated',
        description: displayName ? `This proposal now reflects ${displayName}.` : undefined,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setConfirmOpen(false);
    } catch (e: any) {
      const msg = e?.response?.data ?? 'The customer information could not be updated.';
      toast({
        title: 'Update failed',
        description: typeof msg === 'string' ? msg : undefined,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormRow
      fullWidth
      heading="Customer"
      subHeading="Who this proposal is for — the customer on its project. Separate from the Service Address (the end-user location), which can differ for a drop-ship or multi-site job."
    >
      <Box borderWidth="1px" borderColor={border} borderRadius="md" p={3}>
        <Text fontSize="xx-small" textTransform="uppercase" letterSpacing="0.05em" color={labelColor}>Customer</Text>
        <Text fontSize="sm" fontWeight="semibold">{displayName || (v.projectId ? '—' : 'Select a project first')}</Text>
        {c?.email && <Text fontSize="xs" color={sub}>{c.email}</Text>}
        {c?.phone && <Text fontSize="xs" color={sub}>{c.phone}</Text>}
        {mismatch && (
          <Box mt={3} p={3} borderWidth="1px" borderRadius="md" borderColor="orange.400" bg={warnBg}>
            <Text fontSize="sm" fontWeight="semibold" color="orange.500" mb={1}>⚠ Update your customer information</Text>
            <Text fontSize="xs" color={sub} mb={2}>
              This proposal moved to a project owned by
              {' '}
              <b>{displayName || 'a new customer'}</b>
              , but its saved customer information (legal identity, signatory, and project manager)
              still reflects
              {' '}
              <b>{oldName || 'the previous customer'}</b>
              . The service address, pricing, venues, and scope stay exactly as they are.
            </Text>
            <Button size="sm" colorScheme="orange" isLoading={busy} onClick={refresh}>
              Automatically update customer information
            </Button>
          </Box>
        )}
      </Box>

      {/* dart (2026-07-14) — the forcing confirm on a project switch to a new customer */}
      <AlertDialog
        isOpen={confirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={cancel}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Update customer information?</AlertDialogHeader>
            <AlertDialogBody>
              <Text fontSize="sm">
                You moved this proposal to a project owned by
                {' '}
                <b>{displayName || 'a different customer'}</b>
                {oldName ? ` (it was under ${oldName}).` : '.'}
              </Text>
              <Text fontSize="sm" mt={2}>
                Update its customer information — legal identity, signatory, and project manager — to
                match
                {' '}
                <b>{displayName || 'the new customer'}</b>
                ? The service address, pricing, venues, and scope won&apos;t change.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" onClick={cancel}>
                Cancel
              </Button>
              <Button colorScheme="orange" ml={3} isLoading={busy} onClick={refresh}>
                Yes, update customer information
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </FormRow>
  );
};
CustomerSection.defaultProps = { locked: false };

const ProposalFormBody: React.FC = () => {
  const { id } = useParams<{id: string}>();
  const history = useHistory();
  const proposalKind = new URLSearchParams(useLocation().search).get('kind'); // RNWY 2.0 · P0
  const {
    tryUpdate, tryChangeStatus, data: proposal, status,
  } = useProposal({ id });
  const exportToast = useToast();
  // dart (2026-07-15, Chad) — punt this RNWY proposal into an editable Standard proposal (replaces the
  // scrapped "Non-RNWY PDF"). Also lives on the grid ⋮. Backend versions the title from the project.
  const exportStandardProposal = async (): Promise<void> => {
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      const { data: res } = await axios.post(`${apiUrl}proposal/${id}/export-standard`, {});
      exportToast({ status: 'success', title: 'Standard proposal created', description: 'Opening it to edit.' });
      if ((res as { id?: string })?.id) history.push(`/proposal/${(res as { id: string }).id}`);
    } catch {
      exportToast({ status: 'error', title: 'Export failed', description: 'The standard proposal was not created.' });
    }
  };
  const { terms } = useCompanyTerms();
  const termsBoxBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  // item 287 — approve/decline a proposal straight from the edit screen. APPROVE reuses
  // the SAME wizard the grid uses (auto-creates the WO, assigns project leads, can
  // generate the deposit invoice); DECLINE flips status. Only while Pending — a proposal
  // is editable only when Pending, and Approved-Deposited/Complete are invoice-driven.
  const approvalWizard = useDisclosure();
  const toast = useToast(); // item 1033 — the footer PDF menu owns its error toast
  const isPending = id !== NIL && proposal?.status === ProposalStatus.Pending;
  // item 870 (Chad) — a customer request can't be priced-and-sent while it still lives in the
  // hidden drafts bucket: assign it to one of the customer's REAL projects first (the Customer
  // Requests queue's Assign, or the grid's move-to-project). The Send button explains itself.
  const { data: homeProject } = useProject({ id: proposal?.projectId || NIL });
  const sendBlockReason = (proposal as any)?.isCustomerSubmission && homeProject?.name === 'Customer Submitted Drafts'
    ? 'Assign this request to one of the customer’s projects first — Tools → Customer requests → Assign.'
    : undefined;

  // item 441 — the terminal-state ribbon ("states it's been approved if I open the
  // proposal" — Chad). Pending shows nothing; the PDF twin is the diagonal watermark.
  let statusStamp: React.ReactNode;
  if (proposal && id !== NIL) {
    if (proposal.isVoid) {
      statusStamp = <DocumentStatusStamp tone="negative" label="VOID" detail="This proposal is void." />;
    } else if (proposal.status === ProposalStatus.Approved) {
      statusStamp = <DocumentStatusStamp tone="positive" label="APPROVED" detail="This proposal is locked — duplicate it to start a revision. Hand-off notes stay editable below." />;
    } else if (proposal.status === ProposalStatus.ApprovedDeposited) {
      // item 548 — "In Progress" (deposit invoiced, job underway); consistent with the list pill
      statusStamp = <DocumentStatusStamp tone="positive" label="IN PROGRESS" detail="Deposit invoiced — the job is underway. Locked; duplicate it to start a revision." />;
    } else if (proposal.status === ProposalStatus.Complete) {
      statusStamp = <DocumentStatusStamp tone="positive" label="COMPLETE" detail="The job is complete; this proposal is locked." />;
    } else if (proposal.status === ProposalStatus.Declined) {
      statusStamp = <DocumentStatusStamp tone="neutral" label="DECLINED" detail="Locked — reopen it as Pending from the grid, or duplicate it to revise." />;
    }
  }

  // the text shown for the selected option: the proposal's saved snapshot when
  // the selection hasn't changed, otherwise a live preview of the Settings copy
  const termsTextFor = (option: number): string => {
    if (option === proposal?.termsOption && proposal?.termsText) return proposal.termsText;
    if (option === 1) return terms?.option1Text || '';
    if (option === 2) return terms?.option2Text || '';
    return '';
  };

  const termsSection = (
    <>
      <Divider my={4} />
      <FormRow
        fullWidth
        heading="Terms & Conditions"
        subHeading="Attach one of your two terms options — required (managed in Settings)"
      >
        <Field<number> name="termsOption" parse={(v) => Number(v ?? 0)}>
          {({ input, meta }) => {
            const selected = Number(input.value) || 0;
            // item 555 — the form validates on BLUR, so picking an option (a CHANGE, not a
            // blur) left meta.error stale at "Required" — the field showed the picked terms
            // text yet still flagged "choose a terms option". Drive the error off the LIVE
            // selection instead so it clears the instant a real option is chosen.
            const invalidTerms = meta.touched && selected === 0;
            return (
              <>
                <Select
                  maxW="360px"
                  value={selected}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  aria-label="Terms and conditions option"
                  isInvalid={invalidTerms}
                >
                  <option value={0}>None</option>
                  <option value={1} disabled={!terms?.option1Text}>
                    {terms?.option1Name || 'Option 1'}
                    {!terms?.option1Text ? ' (no text in Settings yet)' : ''}
                  </option>
                  <option value={2} disabled={!terms?.option2Text}>
                    {terms?.option2Name || 'Option 2'}
                    {!terms?.option2Text ? ' (no text in Settings yet)' : ''}
                  </option>
                </Select>
                {invalidTerms && (
                  <Text mt={1} fontSize="sm" color="red.400">Please choose a terms option.</Text>
                )}
                {selected !== 0
                && (
                  <Box
                    mt={3}
                    p={3}
                    bg={termsBoxBg}
                    borderWidth="1px"
                    borderRadius="md"
                    maxH="200px"
                    overflowY="auto"
                  >
                    {/* render the T&C with its formatting (item 95) */}
                    <RichTextView source={termsTextFor(selected)} />
                  </Box>
                )}
              </>
            );
          }}
        </Field>
      </FormRow>
    </>
  );

  // item 647 — RNWY-only gate: wraps children so they render ONLY for an RNWY proposal (kind=rnwy
  // or the legacy isRnwy flag). A plain helper (not a component) so children never remount. There
  // is NO manual "RNWY" toggle — the fork chose the template (item 632).
  const rnwyGated = (node: React.ReactNode) => (
    <Field<boolean> name="isRnwy" subscription={{ value: true }}>
      {({ input: rnwyInput }) => (
        <Field<number> name="kind" subscription={{ value: true }}>
          {({ input: kindInput }) => ((rnwyInput.value || kindInput.value === 1 || proposalKind === 'rnwy') ? <>{node}</> : null)}
        </Field>
      )}
    </Field>
  );

  // sync-only effect components (render null): fork mode, terms, standardized-language defaults.
  // Mounted at the top of Step 1 so they run for every RNWY proposal.
  const rnwySyncs = (
    <>
      <NewProposalKindSync isNew={id === NIL} kind={proposalKind} />
      <RnwyModeFromKind />
      {/* item 647 — the DEFAULT prefills (RNWY terms option + standardized language) run ONLY on a
          NEW proposal's first open. Firing them when opening an EXISTING proposal did a form.change
          on load, marking an untouched proposal "unsaved" (the false-dirty flag). Existing proposals
          keep exactly what was saved. */}
      {id === NIL && <RnwyTermsSync />}
      {id === NIL && <RnwyStandardLanguageSync />}
      {/* item 711 (Chad) — new proposals inherit the project's property name + address */}
      {id === NIL && <ProjectAddressSync />}
    </>
  );

  // item 647 · STEP 1 (Project Foundation) — the RNWY licensing basics: License Location, the
  // per-quote CFG rate card (tees up the whole project's pricing before venues), the Standardized
  // Language (pre-fills from Settings, snapshots to THIS proposal on save), and the internal
  // integrator legal/signatory block. NO venue builder / pricing here (that's Step 2).
  const rnwyFoundationSection = rnwyGated(
    <>
      <Divider my={4} />
      <FormRow
        fullWidth
        heading="RNWY Licensing"
        subHeading="This is an RNWY proposal (chosen at the start) — priced from Schedule B. All three options (Perpetual / 2-Year / 5-Year) are presented; the sale type is chosen at approval. Uses Terms & Conditions Option 2."
      >
        <Box mt={3}>
          <ResponsiveStack>
            <TextInputControl
              name="internalProperty"
              label="Internal Property (License Location)"
              isRequired={false}
              placeholder="Sub-venue(s) within the property (leave blank = whole property)"
              helperText="By default the Licensed Location is the whole property — the project's name + address. Fill this ONLY when licensing a sub-venue (one or more venues inside the larger property); it prints as the licensed areas on the contract."
            />
          </ResponsiveStack>
          {/* item 637 — per-quote CFG rate-card editor MOVED to the top of the venue section (dart
              2026-07-16, Chad — it goes hand-in-hand with the venue pricing). */}
          {/* item 613 — the RNWY Standardized Language: pre-filled from the Settings default and
              SNAPSHOTTED onto this proposal on save (it becomes this quote's own — Settings edits
              never rewrite it afterward). Prints on the RNWY proposal PDF; separate from the SOW. */}
          <Divider my={4} />
          <Box>
            <Text fontWeight="semibold" fontSize="sm" mb={1}>RNWY Standardized Language</Text>
            <Text fontSize="xs" color="gray.500" mb={2}>
              The licensing / module-overview / general-standards copy for this RNWY proposal —
              pre-filled from your Settings default and editable here. Once you save, it&apos;s this
              proposal&apos;s own copy. Prints on the proposal PDF; separate from the Statement of Work.
            </Text>
            <Field<string>
              name="rnwyStandardLanguage"
              component={MarkdownTextInput}
              label="Standardized language"
            />
          </Box>

          {/* item 616/647 — INTERNAL-USE integrator / post-contract legal block. Now READ-ONLY,
              pulled from the CUSTOMER's CRM record (one source of truth). Edit it on the customer.
              Never prints on the customer PDF. */}
          <Divider my={4} />
          <Box>
            <Text fontWeight="semibold" fontSize="sm" mb={1}>Integrator — internal use</Text>
            <Text fontSize="xs" color="gray.500" mb={2}>
              The integrator&apos;s legal identity + signatory for the post-contract legal handoff —
              pulled from this customer&apos;s record so it&apos;s always one source of truth. Internal
              only; never printed on the customer proposal.
            </Text>
            <IntegratorLegalReadOnly />
          </Box>
        </Box>
      </FormRow>
    </>,
  );

  // item 647 · STEP 2 — the answer-once venue builder + the live 3-option pricing preview. This is
  // where "Add venue" lives (Step 1 stops before it). The engine turns the venue inputs × CFG into
  // the three options automatically (item 652 — the recap is the single pricing source of truth).
  const rnwyVenuesSection = rnwyGated(
    <>
      {/* item 861 — live capacity of the linked deployed system vs the venues being quoted */}
      <RnwyCapacityStrip />
      {/* dart (2026-07-16, Chad) — per-quote CFG adjustments sit at the TOP of the venue section; they
          go hand-in-hand with the venue pricing (tweak the rate card for THIS quote, then build venues).
          item 954 — GRAYED while the proposal is priced externally (the rate card can't affect price). */}
      <RnwyManualGate>
        <Box mb={4}>
          <RnwyProposalCfgPanel />
        </Box>
      </RnwyManualGate>
      <FormRow
        fullWidth
        heading="RNWY Venues"
        subHeading="Build each venue — system size + the quantities it controls, plus one-time items. Add as many venues as the job needs; the priced options live on the Pricing step."
      >
        <Box mt={3}>
          <RnwyVenueBuilder />
        </Box>
      </FormRow>
    </>,
  );

  // item 669 — the dedicated Pricing step (Step 4): the engine's three-option preview + the
  // client-facing suggested-resale markup. Moved out of the venue step per Chad.
  const rnwyPricingSection = rnwyGated(
    <>
      <FormRow
        fullWidth
        heading="Pricing"
        subHeading="The engine turns your venues × the CFG rate card into the three options (Perpetual / 2-Year / 5-Year), all presented to the client. You'll choose one at approval when the PO lands."
      >
        {/* item 954 — deals priced OUTSIDE the engine (e.g. sq-ft): manual numbers, engine paused */}
        <Box mt={3}>
          <RnwyManualPricingPanel />
        </Box>
        <RnwyManualGate>
          <Box mt={3}>
            <RnwyOptionsPreview />
          </Box>
        </RnwyManualGate>
      </FormRow>
      <Divider my={4} />
      <RnwySuggestedMarkupField />
    </>,
  );

  // item 647 — these sections are shared between the STANDARD proposal's single "Information
  // Section" (infoSection, below) and the RNWY guided flow (infoSteps). Defined once here so both
  // layouts stay in lockstep.
  const basicInformationSection = (
    <FormRow
      fullWidth
      heading="Basic Information"
      subHeading="Information that will be the main identifiers for the proposal"
    >
      <ResponsiveStack>
        <ProjectSelectField name="projectId" label="Project" />
        <TextInputControl
          name="proposalNumber"
          label="Proposal Number"
          isRequired={false}
          placeholder="Uses your standardized numbering — fill in to override with a custom number"
        />
      </ResponsiveStack>
      <ResponsiveStack>
        {/* item 833/C1 — "Requested By" → "Project Manager", a CRM-staff dropdown */}
        <ProjectManagerField />
        {/* item 219 — the salesperson (from the customer's CRM staff); item 833/C2 relabel. Drives commissions. */}
        <CustomerProjectManagerField />
      </ResponsiveStack>
      <ResponsiveStack>
        <DateInputControl
          name="date"
          label="Date"
          isRequired
        />
      </ResponsiveStack>
    </FormRow>
  );

  // item 647 — Platform moved OUT of Step 1 into Step 3 (the scope step): the modules toggled in
  // Step 2 decide the RNWY side; this generic field is the non-RNWY / Crestron platform that flows
  // to the WO + invoice and (later) the Crestron export.
  const platformSection = (
    <FormRow
      fullWidth
      heading="Platform"
      subHeading="Automatically set from the RNWY modules you selected in Step 2 — it flows to this job's work order + invoice. (The non-RNWY platform is captured on the deferred non-RNWY quote.)"
    >
      {/* item 647 — RNWY platform = the enabled modules "by RNWY" (read-only, auto). The value is
          written to `platform` on save (see applyScaffoldState). */}
      <RnwyPlatformDisplay />
    </FormRow>
  );

  // dart (2026-07-14) — the MANUAL platform for a STANDARD (non-RNWY) proposal. Validation
  // requires platform on non-automated proposals (item 597), but the standard layout had no
  // input for it — so Save silently blocked with no visible field. Restore it (shown only when
  // RNWY is OFF; the RNWY side auto-derives platform from its modules instead).
  const standardPlatformSection = (
    <Field name="isRnwy" subscription={{ value: true }}>
      {({ input: { value: isRnwyOn } }) => (isRnwyOn ? null : (
        <>
          <Divider my={4} />
          <FormRow
            fullWidth
            heading="Platform"
            subHeading="The system / OS this job runs on — flows to the work order + invoice. Required."
          >
            {/* the managed Platform Items list (Settings) — pick an existing platform or type a
                new one (it's added to the list). Same picker projects + work orders use. */}
            <DocumentPlatformField isRequired helperText="Pick from your platform list or type a new one." />
          </FormRow>
        </>
      ))}
    </Field>
  );

  const statementOfWorkSection = (
    <FormRow
      fullWidth
      heading="Statement Of Work"
      subHeading="Detailed description of the work going to be provided"
    >
      <Field<string>
        name="statementOfWork"
        component={MarkdownTextInput}
        label="Work Description"
      />
    </FormRow>
  );

  const handOffSection = (
    <FormRow
      fullWidth
      heading="Hand-Off Notes"
      subHeading="Deployment team ONLY — prints on the Hand-Off PDF and nowhere else. The customer never sees this. Stays editable after approval."
    >
      {/* item 423 — hand-off-only section. item 431 — the content lock protects the
          CONTRACT; hand-off logistics keep evolving, so a locked (approved) proposal
          gets a standalone editor with its own save. */}
      {isPending || id === NIL ? (
        <Field<string>
          name="handOffNotes"
          component={MarkdownTextInput}
          label="Hand-Off Notes (internal)"
        />
      ) : (
        <HandOffNotesEditor proposalId={id} initial={proposal?.handOffNotes ?? ''} />
      )}
    </FormRow>
  );

  const itemSettingsSection = (
    <FormRow
      fullWidth
      heading="Item Settings"
      subHeading="Change how the items are displayed"
    >
      {/* item 261 — the scattered checkbox group is now an aligned toggle grid;
          item 254 — each toggle carries a plain-language explainer. item 234 — the
          PDF price columns: group price is the binding total, MSRP/SRP a suggestion. */}
      {/* item 261 — the scattered checkbox group is now an aligned toggle grid.
          item 710 (Chad) — the two "Itemize" toggles were RETIRED: they never affected the
          PDF (the renderer always itemizes by category), so they were removed as dead controls.
          item 425 — "Show cost on proposal" was removed earlier (cost is internal-only). */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={1}>
        <Field<boolean>
          name="applyDiscountToTaxable"
          component={ToggleInput}
          label="Apply discounts to taxable"
          helperText="Take the discount off the taxable subtotal before tax."
        />
        {/* item 833 (Chad) — line pricing is OFF by default: the PDF shows each line (item + qty)
            with NO per-line pricing, and only the job total at the bottom. Turning these on adds the
            per-line price columns back. */}
        <Field<boolean>
          name="showGroupPriceOnPdf"
          component={ToggleInput}
          label="Itemize group price per line"
          helperText="Off (default): lines show item + quantity only, no per-line price — just the job total."
        />
        <Field<boolean>
          name="showMsrpOnPdf"
          component={ToggleInput}
          label="Itemize SRP per line"
          helperText="Off (default): no suggested-retail column. On: show SRP per line."
        />
        {/* item 833 (Chad) — the "Show Help Desk on PDF" output toggle only appears once Help Desk
            is enabled above (helpDeskTierPricesJson set). Hidden otherwise. */}
        <HelpDeskPdfToggle />
      </SimpleGrid>
      {/* item 841 (Chad) — designate a category as monthly (raw recurring total, separate section) */}
      <MonthlyCategoryField />
    </FormRow>
  );

  // item 710 (Chad) — AUTOMATED (RNWY) output settings live in a self-contained component
  // (RnwyOutputSection, below) so it can read the live form values for the last-option guard.

  // item 257 — the client's received PO authorizing this job
  const receivedPoSection = id !== NIL ? <ReceivedPoAttachments docId={id} /> : null;

  // STANDARD proposal (+ any non-stepMode fallback): the single Information Section, in the
  // original order. The RNWY/Non-RNWY fragments gate THEMSELVES out for a standard proposal, so
  // this renders exactly as before (Service Address → Basic Info → SOW → Hand-off → Item Settings
  // → Terms → Received PO). item 650 — full-width end to end.
  const infoSection = (
    <>
      {rnwySyncs}
      <CustomerSection locked={id !== NIL && Boolean(proposal?.isLocked)} />
      <Divider my={4} />
      <ServiceAddressSection nameField="proposalTitle" fullWidth />
      {rnwyFoundationSection}
      {rnwyVenuesSection}
      <Divider my={4} />
      {basicInformationSection}
      {standardPlatformSection}
      <Divider my={4} />
      {statementOfWorkSection}
      <Divider my={4} />
      {handOffSection}
      {/* item 833 (Chad) — STANDARD Help Desk offer sits ABOVE the PDF Item Settings: enabling it
          here is what makes the "Show Help Desk on PDF" output toggle appear below. (RNWY has its
          own per-option menu, so this is the standard-only path.) */}
      <Divider my={4} />
      <StandardHelpDeskField />
      <Divider my={4} />
      {itemSettingsSection}
      {termsSection}
      {receivedPoSection}
    </>
  );

  // item 647 — RNWY guided flow (Sch-B proposals ONLY; standard proposals use infoSection above).
  // STEP 1 "Project Foundation": who it's for + RNWY licensing / CFG / standardized language /
  // integrator legal + basic info (stops before the venue builder). STEP 2: venues & pricing + the
  // rest. We'll section Step 2 further (into Steps 2/3/4) next.
  const infoSteps = [
    {
      title: 'Step 1 · Project Foundation',
      content: (
        <>
          {rnwySyncs}
          <CustomerSection locked={id !== NIL && Boolean(proposal?.isLocked)} />
          <Divider my={4} />
          <ServiceAddressSection nameField="proposalTitle" fullWidth />
          {rnwyFoundationSection}
          <Divider my={4} />
          {basicInformationSection}
        </>
      ),
    },
    {
      // item 647/669 — Step 2 is the VENUE focus: quantities, venue names, sizes, modules + the
      // one-time item add-ons. Pricing moved to its own step (Step 4). Renders for RNWY + Crestron.
      title: 'Step 2 · Venues',
      content: (
        <>
          {rnwyVenuesSection}
        </>
      ),
    },
    {
      // item 647 — Step 3 is the SCOPE step: Statement of Work + Platform + the finalization
      // details (hand-off, item settings, definitions, terms, received PO).
      title: 'Step 3 · Scope',
      content: (
        <>
          {/* item 654 — auto-builds the full SOW from Steps 1–2 (venues/modules/SwitchPilot) until
              you edit it; a confirmed Regenerate rebuilds from scratch. Renders above the SOW.
              item 994 — it may only act once the saved document is KNOWN (load race clobbered
              custom SOW text on both RNWY and Crestron proposals). */}
          <RnwyScopeManager
            isNew={id === NIL}
            savedSow={proposal?.statementOfWork ?? null}
            savedVenuesJson={(proposal as any)?.rnwyVenuesJson ?? null} // item 1391
            loaded={id === NIL || !!proposal}
          />
          {statementOfWorkSection}
          <Divider my={4} />
          {platformSection}
          <Divider my={4} />
          {handOffSection}
          <Divider my={4} />
          {/* item 661 — Definitions print above the Terms & Conditions */}
          <RnwyDefinitionsSection />
          {termsSection}
          {receivedPoSection}
        </>
      ),
    },
    {
      // item 669 — Step 4 · Pricing: the three-option engine preview + the suggested-resale markup.
      title: 'Step 4 · Pricing',
      content: (
        <>
          {rnwyPricingSection}
        </>
      ),
    },
    {
      // item 670 — Step 5 · Output settings. item 710 (Chad) — for AUTOMATED proposals this is the
      // engine output visibility (Perpetual / 2-yr / 5-yr / Help Desk / SwitchPilot), NOT the old
      // item-settings toggles (which never affected an engine PDF).
      title: 'Step 5 · Output settings',
      content: (
        <>
          <RnwyOutputSection />
        </>
      ),
    },
  ];

  const afterItems = (
    <>
      <Divider my={4} />
      <FormRow
        fullWidth
        heading="Global Discount"
        subHeading="Amount and the description of any discount"
      >
        <ResponsiveStack>
          <CurrencyInputControl
            name="discount"
            label="Amount"
            isRequired={false}
          />
          <TextInputControl
            name="discountDescription"
            label="Description"
            isRequired={false}
          />
        </ResponsiveStack>
      </FormRow>
      <Divider my={4} />
      {/* item 179 — choose the state whose sales-tax rate applies to the taxable lines */}
      <FormRow
        fullWidth
        heading="Sales Tax"
        subHeading="The state whose rate applies to taxable lines (none = no tax). Tax-exempt customers are never charged."
      >
        <SelectInputControl
          name="taxState"
          label="Tax State"
          isRequired={false}
          helperText="Sets the sales-tax rate applied to taxable lines on this document."
        >
          <option value="">— No tax —</option>
          {US_STATES.map((s) => (
            <option key={s.code} value={s.code}>{`${s.code} — ${s.name}`}</option>
          ))}
        </SelectInputControl>
      </FormRow>
      {/* item 839 (Chad) — upload documents straight from the proposal. They write into the SAME
          project document store, auto-filed into a folder named for this proposal (its number), so
          the file shows here AND in the project's Documents tree under that folder — one set of rows,
          two views. Only for a saved proposal (needs a project + number). */}
      {id !== NIL && proposal?.projectId && proposal?.proposalNumber ? (
        <>
          <Divider my={4} />
          <ProjectDocumentsSection
            projectId={proposal.projectId}
            restrictToFolder={proposal.proposalNumber}
            title="Proposal documents"
            subtitleNote="also in the project’s Documents tree"
            emptyText="No files yet. Upload proposal documents — they’ll also appear in the project’s Documents tree."
          />
        </>
      ) : null}
    </>
  );

  // item 647 — Sch-B proposals (RNWY kind=1 / Crestron kind=2, incl. the fresh-from-fork URL
  // kind before first save) get the guided "Step 1" treatment. Standard proposals + every other
  // doc type keep the plain "Information Section". kind is fixed at the fork and never changes
  // mid-edit, so the saved doc + the URL kind fully determine this.
  const isSchB = proposalKind === 'rnwy' || proposalKind === 'crestron'
    || !!proposal?.isRnwy || proposal?.kind === 1 || proposal?.kind === 2;

  return (
    <>
      <DocumentFormScaffold<Proposal, ProposalItem>
        config={{
          stepMode: isSchB, // item 647 — guided step flow (Sch-B proposals only)
          infoSteps: isSchB ? infoSteps : undefined, // item 647 — Project Foundation / Step 2 cards
          // item 647 — progress bar pinned in the sticky header (receives lifted live values)
          stepProgress: isSchB
            ? (values, goToStep) => <RnwyStepProgress values={values} goToStep={goToStep} />
            : undefined,
          // item 668/670 — RNWY has NO separate Items/"Pricing & tax" section: one-time items are the
          // catalog add-ons in Venues + SwitchPilot, and RNWY doesn't discount (tax is added at
          // invoice). Drop the whole trailing section; item settings live on Step 5 · Output settings.
          hideItemsSection: isSchB,
          title: 'PROPOSAL',
          saveLabel: 'Save Proposal',
          // a proposal locks the moment it leaves Pending (item 43): approved/deposited/
          // complete/void proposals can't be saved. Hiding Save also disables auto-save
          // (scaffold: autoSaveEnabled = !hideSave), so a locked proposal no longer spams
          // failed 400s — the status banner tells the user to duplicate it to edit.
          hideSave: id !== NIL && Boolean(proposal?.isLocked),
          icon: IconProposals,
          iconWhite: IconProposalsWhite,
          doc: proposal,
          status,
          tryUpdate,
          validate,
          getItems: (doc) => doc.proposalItems,
          getCategoryNames: (doc) => doc.itemCategories,
          applyScaffoldState: (values, state) => {
            values.itemCategories = state.categoryNames;
            values.proposalItems = state.items;
            values.id = state.id;
            // item 833 (Chad) — line pricing is OFF by default now: a new proposal shows each line
            // (item + qty) with no per-line price, only the job total. (Was on per item 234.)
            if (values.showGroupPriceOnPdf === undefined) values.showGroupPriceOnPdf = false;
            if (values.showMsrpOnPdf === undefined) values.showMsrpOnPdf = false;
            // item 833 (Chad) — a STANDARD proposal auto-selects Terms option 1 from Settings; an
            // automated (RNWY) proposal keeps option 2. Only when nothing's chosen yet (0/unset),
            // so a manual pick and the RnwyTermsSync default are never clobbered.
            if (!values.termsOption) values.termsOption = isSchB ? 2 : 1;
            // item 710 — default the RNWY output-visibility toggles on when untouched
            if (values.showPerpetualOnPdf === undefined) values.showPerpetualOnPdf = true;
            if (values.showTwoYearOnPdf === undefined) values.showTwoYearOnPdf = true;
            if (values.showFiveYearOnPdf === undefined) values.showFiveYearOnPdf = true;
            if (values.showHelpDeskOnPdf === undefined) values.showHelpDeskOnPdf = true;
            if (values.showDefinitionsOnPdf === undefined) values.showDefinitionsOnPdf = true; // item 1285
            if (values.showSwitchPilotOnPdf === undefined) values.showSwitchPilotOnPdf = true;
            if (values.showLinePricesOnPdf === undefined) values.showLinePricesOnPdf = true; // item 1331
            // item 647 — an automated proposal's platform IS its RNWY modules ("<modules> by RNWY").
            // Derive it on save so it flows to the WO + invoice (no manual entry on the RNWY side).
            if (isSchB) {
              const derived = rnwyPlatformLabel((values as { rnwyVenuesJson?: string | null }).rnwyVenuesJson);
              if (derived) (values as { platform?: string }).platform = derived;
            }
          },
          ItemsElement: ProposalItemsFormElement,
          infoSection,
          afterItems,
          statusStamp,
          // item 287/577 — approve/decline in-app from the edit screen (Pending only);
          // the PRIMARY cluster of the footer action bar.
          // item 870 (Chad) — "Approve" was doing double duty on customer submissions: the
          // guided wizard (turn a sold deal into WO + invoice) vs okaying a client request.
          // On a submission the wizard button reads "Process approval" — it PROCESSES the
          // client's approval; the customer's own Approve lives in their portal.
          // item 885 (Chad) — the guided wizard is UNAVAILABLE on a customer request until
          // we've confirmed it: price → SEND back (email w/ PDF + approve link + portal hub).
          // Only once sent does the normal lifecycle open up (client approves → Process
          // approval → WO → invoice). Decline stays available throughout.
          footerPrimary: id !== NIL && isPending ? (
            <>
              {!((proposal as any)?.isCustomerSubmission && !(proposal as any)?.sentDate) && (
                <Button
                  size="sm"
                  leftIcon={<FiCheck />}
                  onClick={approvalWizard.onOpen}
                >
                  {(proposal as any)?.isCustomerSubmission ? 'Process approval' : 'Approve'}
                </Button>
              )}
              <Button
                size="sm"
                variant="danger"
                leftIcon={<FiX />}
                onClick={() => tryChangeStatus(id, ProposalStatus.Declined)}
              >
                Decline
              </Button>
            </>
          ) : undefined,
          // item 31/577 — the UTILITY cluster: PDF/send, plus jumps to the sales
          // attribution (customer CRM record) and the commissions it generated.
          footerActions: id !== NIL ? (
            <>
              {/* item 1033 (Chad: "this button forest is out of hand") — the utility cluster
                  collapses to PDF ▾ + More ▾ so Send (& publish) reads as THE action.
                  PDF ▾ = item 681(C) preview + download + the dart-2026-07-15 standard export;
                  More ▾ = the item 31/577 sales-attribution + commissions jumps. */}
              <Menu isLazy>
                <MenuButton as={Button} size="sm" variant="ghost" leftIcon={<FaFilePdf />} rightIcon={<FiChevronDown />}>
                  PDF
                </MenuButton>
                <Portal>
                  <MenuList fontSize="sm">
                    <MenuItem
                      icon={<FiEye />}
                      onClick={() => openDocumentPdf(`proposal/${id}/pdf`, true).catch(() => toast({
                        status: 'error', title: 'Could not open the PDF', duration: 6000, isClosable: true,
                      }))}
                    >
                      Preview PDF
                    </MenuItem>
                    <MenuItem
                      icon={<FaFilePdf />}
                      onClick={() => openDocumentPdf(`proposal/${id}/pdf`).catch(() => toast({
                        status: 'error', title: 'Could not open the PDF', duration: 6000, isClosable: true,
                      }))}
                    >
                      Download PDF
                    </MenuItem>
                    {(proposal?.isRnwy || proposal?.kind === 1 || proposal?.kind === 2) && (
                      <MenuItem icon={<FiDownload />} onClick={exportStandardProposal}>Export standard proposal</MenuItem>
                    )}
                  </MenuList>
                </Portal>
              </Menu>
              {/* item 576 — the SAME send flow as the grid's "Send to client" (this footer
                  previously skipped minting the public accept link): Pending mints + attaches
                  the approve link, other statuses send status-aware copy from the server */}
              {/* item 1031 (Chad) — until the request has been sent, the button spells out that
                  sending also publishes pricing to the customer's external portal */}
              <ProposalSendControl
                proposalId={id}
                status={proposal?.status}
                disabledReason={sendBlockReason}
                publishToPortal={!!(proposal as any)?.isCustomerSubmission && !(proposal as any)?.sentDate}
              />
              <Menu isLazy>
                <MenuButton as={Button} size="sm" variant="ghost" rightIcon={<FiChevronDown />}>
                  More
                </MenuButton>
                <Portal>
                  <MenuList fontSize="sm">
                    {proposal?.customerId && (
                      <MenuItem icon={<FiUser />} onClick={() => history.push(`/customer/${proposal.customerId}`)}>
                        Sales tracking
                      </MenuItem>
                    )}
                    <MenuItem icon={<FiDollarSign />} onClick={() => history.push(`/my-book?proposal=${id}`)}>
                      Commissions
                    </MenuItem>
                  </MenuList>
                </Portal>
              </Menu>
            </>
          ) : undefined,
        }}
      />
      {/* item 287 — the SAME approval wizard the grid opens (auto-WO + lead split +
        optional deposit invoice). Refetch on close keeps the form's status fresh. */}
      <ProposalApprovalWizard
        isOpen={approvalWizard.isOpen}
        onClose={approvalWizard.onClose}
        proposal={proposal}
      />
    </>
  );
};

// RNWY 2.0 · P0 — the fork gate: a NEW proposal with no ?kind= yet shows the template chooser;
// picking one routes on with ?kind= (preserving projectId) and the form renders for that template.
// Wrapper hooks are stable; the body mounts only once a kind is chosen (or when editing existing).
const ProposalForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const search = new URLSearchParams(useLocation().search);
  const kind = search.get('kind');
  const projectId = search.get('projectId');

  if (id === NIL && !kind) {
    return (
      <NewProposalChoice
        onPick={(k) => history.replace(`/proposal/${NIL}?kind=${k}${projectId ? `&projectId=${projectId}` : ''}`)}
      />
    );
  }
  return <ProposalFormBody />;
};

export default withAuthenticationRequired(ProposalForm);
